import constants from "constants";
import { flattenErrorStrings } from "utils/b2cRedirectErrors";

const DEFAULT_MAX_RETRIES = 8;
const DEFAULT_RETRY_DELAY_MS = 200;

/** @param {unknown} error */
export function isInteractionInProgressError(error) {
  return flattenErrorStrings(error)
    .join(" ")
    .toLowerCase()
    .includes("interaction_in_progress");
}

export function setPendingLoginRedirectFlag() {
  sessionStorage.setItem(
    constants.sessionStorageKeys.pendingLoginRedirect,
    "1",
  );
}

export function clearPendingLoginRedirectFlag() {
  sessionStorage.removeItem(
    constants.sessionStorageKeys.pendingLoginRedirect,
  );
}

export function isPendingLoginRedirectFlagSet() {
  return (
    sessionStorage.getItem(
      constants.sessionStorageKeys.pendingLoginRedirect,
    ) === "1"
  );
}

let loginRedirectChain = null;

/**
 * Start B2C loginRedirect with retries when MSAL reports interaction_in_progress.
 * Concurrent calls share one in-flight chain so automatic + manual login do not race.
 *
 * @param {import("@azure/msal-browser").IPublicClientApplication} instance
 * @param {import("@azure/msal-browser").RedirectRequest} request
 * @param {{ skipPendingFlag?: boolean, maxRetries?: number, retryDelayMs?: number }} [options]
 * @returns {Promise<void>}
 */
export function safeMsalLoginRedirect(instance, request, options = {}) {
  const {
    skipPendingFlag = false,
    maxRetries = DEFAULT_MAX_RETRIES,
    retryDelayMs = DEFAULT_RETRY_DELAY_MS,
  } = options;

  if (!skipPendingFlag) {
    setPendingLoginRedirectFlag();
  }

  if (loginRedirectChain) {
    return loginRedirectChain;
  }

  const attempt = (retryCount) =>
    instance.loginRedirect(request).catch((err) => {
      if (isInteractionInProgressError(err) && retryCount < maxRetries) {
        return new Promise((resolve, reject) => {
          window.setTimeout(() => {
            attempt(retryCount + 1).then(resolve).catch(reject);
          }, retryDelayMs * (retryCount + 1));
        });
      }
      clearPendingLoginRedirectFlag();
      return Promise.reject(err);
    });

  loginRedirectChain = attempt(0)
    .catch((err) => {
      console.error("MSAL loginRedirect failed:", err);
      throw err;
    })
    .finally(() => {
      loginRedirectChain = null;
    });

  return loginRedirectChain;
}
